<script setup>
import CaretLeftIcon from '@iconify-vue/ph/caret-left';
import CaretRightIcon from '@iconify-vue/ph/caret-right';
import DownloadSimpleIcon from '@iconify-vue/ph/download-simple';
import MagnifyingGlassMinusIcon from '@iconify-vue/ph/magnifying-glass-minus';
import MagnifyingGlassPlusIcon from '@iconify-vue/ph/magnifying-glass-plus';
import XIcon from '@iconify-vue/ph/x';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import { useRoute } from '@js/utils/route';
import { AnimatePresence, Motion } from 'motion-v';
import {
    DialogClose,
    DialogContent,
    DialogOverlay,
    DialogPortal,
    DialogRoot,
    DialogTitle,
    VisuallyHidden,
} from 'reka-ui';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

const {
    leftImages,
    rightImages,
    leftLabel = 'Original',
    rightLabel = 'Générée',
    align = 'left',
    size = 'medium',
} = defineProps({
    leftImages: {
        type: Array,
        required: true,
    },
    rightImages: {
        type: Array,
        required: true,
    },
    leftLabel: {
        type: String,
        required: false,
    },
    rightLabel: {
        type: String,
        required: false,
    },
    align: {
        type: String,
        required: false,
    },
    size: {
        type: String,
        required: false,
    },
});

const selectedIndex = ref(null);
const allImages = computed(() => {
    const maxLen = Math.max(leftImages.length, rightImages.length);
    const pairs = [];
    for (let i = 0; i < maxLen; i++) {
        pairs.push({
            left: leftImages[i] || null,
            right: rightImages[i] || null,
        });
    }
    return pairs;
});

const selectedPair = computed(() =>
    selectedIndex.value !== null ? allImages.value[selectedIndex.value] : null,
);

const selectedLeftImage = computed(() =>
    selectedPair.value ? selectedPair.value.left : null,
);

const selectedRightImage = computed(() =>
    selectedPair.value ? selectedPair.value.right : null,
);

const scale = ref(1);
const translateX = ref(0);
const translateY = ref(0);
const isDragging = ref(false);
const dragStart = ref({ x: 0, y: 0 });

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const SCALE_STEP = 0.5;

const hasPrev = computed(
    () => selectedIndex.value !== null && selectedIndex.value > 0,
);
const hasNext = computed(
    () =>
        selectedIndex.value !== null &&
        selectedIndex.value < allImages.value.length - 1,
);

function resetZoom() {
    scale.value = 1;
    translateX.value = 0;
    translateY.value = 0;
}

function goToPrev() {
    if (!hasPrev.value) return;
    resetZoom();
    selectedIndex.value--;
}

function goToNext() {
    if (!hasNext.value) return;
    resetZoom();
    selectedIndex.value++;
}

function onKeydown(e) {
    if (selectedIndex.value === null) return;
    if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrev();
    } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToNext();
    }
}

onMounted(() => window.addEventListener('keydown', onKeydown));
onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown));

function zoomIn() {
    scale.value = Math.min(MAX_SCALE, scale.value + SCALE_STEP);
    if (scale.value === MIN_SCALE) {
        translateX.value = 0;
        translateY.value = 0;
    }
}

function zoomOut() {
    scale.value = Math.max(MIN_SCALE, scale.value - SCALE_STEP);
    if (scale.value === MIN_SCALE) {
        translateX.value = 0;
        translateY.value = 0;
    }
}

function onWheel(e) {
    e.preventDefault();
    if (e.deltaY < 0) zoomIn();
    else zoomOut();
}

function onPointerDown(e) {
    if (scale.value <= MIN_SCALE) return;
    isDragging.value = true;
    dragStart.value = {
        x: e.clientX - translateX.value,
        y: e.clientY - translateY.value,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
}

function onPointerMove(e) {
    if (!isDragging.value) return;
    translateX.value = e.clientX - dragStart.value.x;
    translateY.value = e.clientY - dragStart.value.y;
}

function onPointerUp() {
    isDragging.value = false;
}

function openLightbox(index) {
    selectedIndex.value = index;
}

const isLightboxOpen = computed({
    get: () => selectedIndex.value !== null,
    set: (value) => {
        if (!value) {
            selectedIndex.value = null;
        }
    },
});

watch(isLightboxOpen, (open) => {
    if (!open) resetZoom();
});

const { route } = useRoute();

const visibleLeftStackImages = computed(() => leftImages.slice(0, 3));
const visibleRightStackImages = computed(() => rightImages.slice(0, 3));

function getFileName(url) {
    return url.split('/').pop().split('?')[0];
}

function download(url) {
    const link = document.createElement('a');
    link.href = route('download-proxy', { url });
    link.download = getFileName(url);
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

const sizeClasses = computed(() => {
    return {
        small: {
            single: 'size-15',
            double: 'w-21 h-17',
            triple: 'w-28 h-19',
            img: 'size-15 max-w-15',
        },
        medium: {
            single: 'size-30',
            double: 'w-36 h-32',
            triple: 'w-42 h-34',
            img: 'size-30 max-w-30',
        },
        large: {
            single: 'size-45',
            double: 'w-51 h-47',
            triple: 'w-57 h-49',
            img: 'size-45 max-w-45',
        },
    }[size];
});

function stackContainerClass(images) {
    const len = images.length;
    if (len === 1) return sizeClasses.value.single;
    if (len === 2) return sizeClasses.value.double;
    if (len >= 3) return sizeClasses.value.triple;
    return '';
}
</script>

<template>
    <BaseCell :align="align">
        <div class="flex items-center gap-4">
            <div
                v-if="leftImages.length > 0"
                class="relative cursor-zoom-in"
                :class="stackContainerClass(leftImages)"
                @click="openLightbox(0)"
            >
                <img
                    v-for="(src, index) in visibleLeftStackImages"
                    :key="'left-' + index"
                    :src="src"
                    class="absolute rounded border border-gray-300 bg-white object-contain"
                    :class="sizeClasses.img"
                    :style="{
                        top: `calc(var(--spacing) * ${index * 2})`,
                        left: `calc(var(--spacing) * ${index * 6})`,
                        zIndex: visibleLeftStackImages.length - index,
                    }"
                    alt="Image originale"
                />
                <span
                    v-if="leftImages.length > 3"
                    class="absolute -right-2 -bottom-2 z-10 rounded-full bg-indigo-600 px-1.5 py-0.5 text-xs leading-none text-white shadow"
                >
                    +{{ leftImages.length - 3 }}
                </span>
            </div>

            <div v-if="rightImages.length > 0" class="flex items-center gap-4">
                <svg
                    class="size-5 shrink-0 text-gray-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                >
                    <path
                        fill-rule="evenodd"
                        d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z"
                        clip-rule="evenodd"
                    />
                </svg>

                <div
                    class="relative cursor-zoom-in"
                    :class="stackContainerClass(rightImages)"
                    @click="openLightbox(0)"
                >
                    <img
                        v-for="(src, index) in visibleRightStackImages"
                        :key="'right-' + index"
                        :src="src"
                        class="absolute rounded border border-emerald-400 bg-white object-contain"
                        :class="sizeClasses.img"
                        :style="{
                            top: `calc(var(--spacing) * ${index * 2})`,
                            left: `calc(var(--spacing) * ${index * 6})`,
                            zIndex: visibleRightStackImages.length - index,
                        }"
                        alt="Image générée"
                    />
                    <span
                        v-if="rightImages.length > 3"
                        class="absolute -right-2 -bottom-2 z-10 rounded-full bg-emerald-600 px-1.5 py-0.5 text-xs leading-none text-white shadow"
                    >
                        +{{ rightImages.length - 3 }}
                    </span>
                </div>
            </div>
        </div>

        <DialogRoot v-model:open="isLightboxOpen">
            <DialogPortal forceMount>
                <AnimatePresence>
                    <Motion
                        v-if="isLightboxOpen"
                        key="overlay"
                        :initial="{ opacity: 0 }"
                        :animate="{ opacity: 1 }"
                        :exit="{ opacity: 0 }"
                        :transition="{ duration: 0.2 }"
                        class="fixed inset-0 z-50"
                    >
                        <DialogOverlay
                            forceMount
                            class="size-full bg-black/80"
                        />
                    </Motion>

                    <Motion
                        v-if="isLightboxOpen"
                        key="content"
                        :initial="{ opacity: 0, scale: 0.96 }"
                        :animate="{ opacity: 1, scale: 1 }"
                        :exit="{ opacity: 0, scale: 0.96 }"
                        :transition="{
                            type: 'spring',
                            duration: 0.3,
                            bounce: 0.1,
                        }"
                        class="pointer-events-none fixed inset-0 z-50 flex items-center justify-center"
                    >
                        <DialogContent
                            forceMount
                            class="pointer-events-auto relative max-h-[90vh] max-w-[90vw] focus:outline-none"
                            :aria-describedby="undefined"
                        >
                            <VisuallyHidden>
                                <DialogTitle />
                            </VisuallyHidden>

                            <div
                                class="absolute -top-12 right-0 left-0 flex items-center justify-between"
                            >
                                <div class="flex gap-2">
                                    <button
                                        class="aspect-square rounded-full bg-indigo-600 p-2 text-white shadow-lg duration-150 enabled:hover:bg-indigo-700 disabled:opacity-40"
                                        :disabled="scale <= 1"
                                        title="Dézoomer"
                                        @click="zoomOut"
                                    >
                                        <MagnifyingGlassMinusIcon
                                            class="size-5"
                                        />
                                    </button>

                                    <button
                                        class="aspect-square rounded-full bg-indigo-600 p-2 text-white shadow-lg duration-150 enabled:hover:bg-indigo-700 disabled:opacity-40"
                                        :disabled="scale >= 5"
                                        title="Zoomer"
                                        @click="zoomIn"
                                    >
                                        <MagnifyingGlassPlusIcon
                                            class="size-5"
                                        />
                                    </button>
                                </div>

                                <DialogClose
                                    class="aspect-square rounded-full bg-indigo-600 p-2 text-white shadow-lg duration-150 hover:bg-indigo-700"
                                >
                                    <XIcon class="size-5" />
                                </DialogClose>
                            </div>

                            <button
                                title="Précédent"
                                @click="goToPrev"
                                :disabled="!hasPrev"
                                class="absolute top-1/2 -left-12 z-50 aspect-square -translate-y-1/2 rounded-full bg-indigo-600 p-2 text-white shadow-lg duration-150 enabled:hover:bg-indigo-700 disabled:opacity-40"
                            >
                                <CaretLeftIcon class="size-5" />
                            </button>

                            <button
                                title="Suivant"
                                @click="goToNext"
                                :disabled="!hasNext"
                                class="absolute top-1/2 -right-12 z-50 aspect-square -translate-y-1/2 rounded-full bg-indigo-600 p-2 text-white shadow-lg duration-150 enabled:hover:bg-indigo-700 disabled:opacity-40"
                            >
                                <CaretRightIcon class="size-5" />
                            </button>

                            <div
                                class="flex items-stretch gap-4"
                                @wheel="onWheel"
                                @dblclick="resetZoom"
                            >
                                <div class="flex flex-col items-center gap-2">
                                    <div class="flex items-center gap-2">
                                        <span
                                            class="rounded-full bg-indigo-600 px-3 py-1 text-xs font-medium text-white shadow"
                                        >
                                            {{ leftLabel }}
                                        </span>
                                        <button
                                            v-if="selectedLeftImage"
                                            class="aspect-square rounded-full bg-indigo-600 p-1.5 text-white shadow-lg duration-150 hover:bg-indigo-700"
                                            title="Télécharger l'original"
                                            @click="download(selectedLeftImage)"
                                        >
                                            <DownloadSimpleIcon
                                                class="size-4"
                                            />
                                        </button>
                                    </div>
                                    <div
                                        v-if="selectedLeftImage"
                                        class="overflow-hidden rounded-lg bg-white shadow-lg"
                                    >
                                        <img
                                            :src="selectedLeftImage"
                                            :style="{
                                                transform: `scale(${scale}) translate(${translateX / scale}px, ${translateY / scale}px)`,
                                            }"
                                            class="max-h-[80vh] max-w-[42vw] object-contain transition-transform duration-150"
                                            :class="{
                                                'cursor-grab':
                                                    scale > 1 && !isDragging,
                                                'cursor-grabbing': isDragging,
                                                '!duration-0': isDragging,
                                            }"
                                            draggable="false"
                                            @pointerdown="onPointerDown"
                                            @pointermove="onPointerMove"
                                            @pointerup="onPointerUp"
                                            alt="Image originale"
                                        />
                                    </div>
                                    <div
                                        v-else
                                        class="flex h-64 w-[42vw] items-center justify-center rounded-lg bg-white shadow-lg"
                                    >
                                        <p class="text-sm text-gray-400">
                                            Aucune image
                                        </p>
                                    </div>
                                </div>

                                <div class="flex flex-col items-center gap-2">
                                    <div class="flex items-center gap-2">
                                        <span
                                            class="rounded-full bg-emerald-600 px-3 py-1 text-xs font-medium text-white shadow"
                                        >
                                            {{ rightLabel }}
                                        </span>
                                        <button
                                            v-if="selectedRightImage"
                                            class="aspect-square rounded-full bg-emerald-600 p-1.5 text-white shadow-lg duration-150 hover:bg-emerald-700"
                                            title="Télécharger la générée"
                                            @click="
                                                download(selectedRightImage)
                                            "
                                        >
                                            <DownloadSimpleIcon
                                                class="size-4"
                                            />
                                        </button>
                                    </div>
                                    <div
                                        v-if="selectedRightImage"
                                        class="overflow-hidden rounded-lg bg-white shadow-lg ring-2 ring-emerald-400"
                                    >
                                        <img
                                            :src="selectedRightImage"
                                            :style="{
                                                transform: `scale(${scale}) translate(${translateX / scale}px, ${translateY / scale}px)`,
                                            }"
                                            class="max-h-[80vh] max-w-[42vw] object-contain transition-transform duration-150"
                                            :class="{
                                                'cursor-grab':
                                                    scale > 1 && !isDragging,
                                                'cursor-grabbing': isDragging,
                                                '!duration-0': isDragging,
                                            }"
                                            draggable="false"
                                            @pointerdown="onPointerDown"
                                            @pointermove="onPointerMove"
                                            @pointerup="onPointerUp"
                                            alt="Image générée"
                                        />
                                    </div>
                                    <div
                                        v-else
                                        class="flex h-64 w-[42vw] items-center justify-center rounded-lg bg-white shadow-lg"
                                    >
                                        <p class="text-sm text-gray-400">
                                            Aucune image
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </DialogContent>
                    </Motion>
                </AnimatePresence>
            </DialogPortal>
        </DialogRoot>
    </BaseCell>
</template>
